// src/ui/Chip.tsx (T1): chips (filters, choices) and the segmented control (joined chips, the phone filters).
// A chip is a toggle: aria-pressed carries the state, so it is never color alone. Root classes: hb-chip,
// hb-chip-group, hb-segmented.
import type { ButtonHTMLAttributes, ComponentChildren } from "preact";
import { Link } from "../app/router";
import { cx } from "./cx";
import { Icon, type IconName } from "./Icon";

type ChipProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "class" | "className" | "icon"> & {
  pressed?: boolean;
  /** A count shown after the label in a quieter color ("Loved 6"). */
  count?: number;
  icon?: IconName;
  class?: string;
  children?: ComponentChildren;
};

export function Chip(props: ChipProps) {
  const { pressed, count, icon, class: extra, children, ...rest } = props;
  return (
    <button type="button" {...rest} class={cx("hb-chip", "chip", extra)} aria-pressed={pressed === undefined ? undefined : pressed ? "true" : "false"}>
      {icon ? <Icon name={icon} size="sm" /> : null}
      {children}
      {count !== undefined ? <span class="n">{count}</span> : null}
    </button>
  );
}

/** A chip that navigates (a room shortcut, a filter kept in the URL). `current` marks the active one. */
export function ChipLink(props: { to: string; current?: boolean; count?: number; class?: string; children?: ComponentChildren }) {
  return (
    <Link to={props.to} class={cx("hb-chip", "chip", props.current && "is-on", props.class)} aria-current={props.current ? "page" : undefined}>
      {props.children}
      {props.count !== undefined ? <span class="n">{props.count}</span> : null}
    </Link>
  );
}

/** A wrapping row of chips with an accessible group name. */
export function ChipGroup(props: { label: string; class?: string; children?: ComponentChildren }) {
  return (
    <div class={cx("hb-chip-group", props.class)} role="group" aria-label={props.label}>
      {props.children}
    </div>
  );
}

export type SegmentOption<V extends string> = { value: V; label: ComponentChildren; count?: number; title?: string };

/** One choice among a few, as joined chips (D's .chip-join). Keyboard: Tab to the group, arrows move. */
export function Segmented<V extends string>(props: {
  label: string;
  options: readonly SegmentOption<V>[];
  value: V;
  onChange: (value: V) => void;
  class?: string;
}) {
  const { options, value, onChange } = props;
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    const i = options.findIndex((o) => o.value === value);
    const next = options[(i + (e.key === "ArrowRight" ? 1 : options.length - 1)) % options.length];
    if (!next) return;
    e.preventDefault();
    onChange(next.value);
    const group = e.currentTarget as HTMLElement;
    requestAnimationFrame(() => group.querySelector<HTMLButtonElement>(`[data-value="${CSS.escape(next.value)}"]`)?.focus());
  };
  return (
    <div class={cx("hb-segmented", "chip-join", props.class)} role="group" aria-label={props.label} onKeyDown={onKeyDown}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          class="chip"
          data-value={o.value}
          title={o.title}
          aria-pressed={o.value === value ? "true" : "false"}
          tabIndex={o.value === value ? 0 : -1}
          onClick={() => onChange(o.value)}
        >
          {o.label}
          {o.count !== undefined ? <span class="n">{o.count}</span> : null}
        </button>
      ))}
    </div>
  );
}
